export default class InstantPrefetch {
    constructor() {
        this.PREFETCH_COOLDOWN = 1111;
        this.lastClickTime = 0;
        this.allowQueryString = false;
        this.allowExternalLinks = false;
        this.whitelist = false;
        this.userAgentVersion = undefined;
        this.prefetchDelay = 65;
        this.prefetchSet = new Set();
        this.mutationObserver = null;
        this.prefetchTimeout = null;
        this.setupPrefetch();
        this.observeDOMChanges();
    }
    handleTouchStart(event) {
        this.lastClickTime = performance.now();
        const targetLink = event.target.closest("a");
        if (this.isValidLink(targetLink)) {
            this.prefetchLink(targetLink.href, "high");
        }
    }
    handleMouseOver(event) {
        if (performance.now() - this.lastClickTime < this.PREFETCH_COOLDOWN)
            return;
        if (!("closest" in event.target))
            return;
        const targetLink = event.target.closest("a");
        if (this.isValidLink(targetLink)) {
            targetLink?.addEventListener("mouseout", this.handleMouseOut.bind(this), { passive: true });
            this.prefetchTimeout = setTimeout(() => {
                this.prefetchLink(targetLink.href, "high");
            }, this.prefetchDelay);
        }
    }
    handleMouseOut(event) {
        if (event.relatedTarget && event.target.closest("a") == event.relatedTarget.closest("a"))
            return;
        clearTimeout(this.prefetchTimeout);
    }
    handleClick(event) {
        if (performance.now() - this.lastClickTime < this.PREFETCH_COOLDOWN)
            return;
        const targetLink = event.target.closest("a");
        if (event.which > 1 || event.metaKey || event.ctrlKey)
            return;
        if (!targetLink)
            return;
        event.preventDefault();
        targetLink?.addEventListener("click", (event) => {
            if (event.detail !== 1337)
                return;
            event.preventDefault();
        }, { capture: true, passive: false, once: true });
        const newClickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: 1337
        });
        targetLink.dispatchEvent(newClickEvent);
    }
    isValidLink(linkElement) {
        if (linkElement && linkElement.href && (!this.whitelist || "instant" in linkElement.dataset)) {
            if (linkElement.origin != location.origin) {
                if (!(this.allowExternalLinks || "instant" in linkElement.dataset) || !this.userAgentVersion)
                    return false;
            }
            if (["http:", "https:"].includes(linkElement.protocol) &&
                (linkElement.protocol != location.protocol || "http:" != linkElement.protocol || "https:" != location.protocol) &&
                (this.allowQueryString || !linkElement.search || "instant" in linkElement.dataset) &&
                !(linkElement.hash && linkElement.pathname + linkElement.search == location.pathname + location.search || "noInstant" in linkElement.dataset)) {
                return true;
            }
        }
        return false;
    }
    prefetchLink(url, priority = "auto") {
        if (this.prefetchSet.has(url))
            return;
        const linkElement = document.createElement("link");
        linkElement.rel = "prefetch";
        linkElement.href = url;
        linkElement["fetchPriority"] = priority;
        linkElement.as = "document";
        document.head.appendChild(linkElement);
        this.prefetchSet.add(url);
    }
    setupPrefetch() {
        if (!document.createElement("link").relList.supports("prefetch"))
            return;
        const hasVaryAccept = "instantVaryAccept" in document.body.dataset || "Shopify" in window;
        const chromeIndex = navigator.userAgent.indexOf("Chrome/");
        if (chromeIndex > -1) {
            this.userAgentVersion = parseInt(navigator.userAgent.substring(chromeIndex + "Chrome/".length));
        }
        if (hasVaryAccept && this.userAgentVersion && this.userAgentVersion < 110)
            return;
        const hasMousedownShortcut = "instantMousedownShortcut" in document.body.dataset;
        this.allowQueryString = "instantAllowQueryString" in document.body.dataset;
        this.allowExternalLinks = "instantAllowExternalLinks" in document.body.dataset;
        this.whitelist = "instantWhitelist" in document.body.dataset;
        const options = { capture: true, passive: true };
        let useMouseover = false;
        let useTouchstart = false;
        let useMousedown = false;
        let useViewport = false;
        if ("instantIntensity" in document.body.dataset) {
            const intensity = document.body.dataset.instantIntensity;
            if (intensity.startsWith("mousedown")) {
                useMousedown = true;
                if (intensity === "mousedown-only") {
                    useMouseover = true;
                }
            }
            else if (intensity.startsWith("viewport")) {
                try {
                    const connection = navigator["connection"];
                    console.log(connection);
                    const isSlowConnection = connection && (connection["saveData"] || (connection["effectiveType"] && connection["effectiveType"].includes("2g")));
                    if (!isSlowConnection) {
                        useViewport = true;
                    }
                }
                catch { }
            }
            else {
                const delay = parseInt(intensity);
                if (!isNaN(delay)) {
                    this.prefetchDelay = delay;
                }
            }
        }
        if (!useMouseover) {
            document.addEventListener("touchstart", this.handleTouchStart.bind(this), options);
            useTouchstart = true;
        }
        if (useMousedown) {
            if (!hasMousedownShortcut) {
                document.addEventListener("mousedown", this.handleMouseOver.bind(this), options);
            }
        }
        else {
            document.addEventListener("mouseover", this.handleMouseOver.bind(this), options);
            useMouseover = true;
        }
        if (hasMousedownShortcut) {
            document.addEventListener("mousedown", this.handleClick.bind(this), options);
            useMousedown = true;
        }
        if (useViewport) {
            let requestIdleCallback = window.requestIdleCallback;
            if (!requestIdleCallback) {
                requestIdleCallback = function (callback, options) {
                    return setTimeout(callback, 0);
                };
            }
            requestIdleCallback(() => {
                const intersectionCallback = (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const targetLink = entry.target;
                            observer.unobserve(targetLink);
                            this.prefetchLink(targetLink.href);
                        }
                    });
                };
                const observer = new IntersectionObserver(intersectionCallback);
                document.querySelectorAll("a").forEach(linkElement => {
                    if (this.isValidLink(linkElement)) {
                        observer.observe(linkElement);
                    }
                });
            }, { timeout: 1500 });
        }
    }
    observeDOMChanges() {
        this.mutationObserver = new MutationObserver(() => {
            // Re-setup prefetch when DOM changes
            this.setupPrefetch();
        });
        this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    }
}
